import { createRouter, createWebHistory } from 'vue-router'
import Bugsnag from '@bugsnag/js'
import Default from '@/layouts/Default.vue'
import HomePage from '@/layouts/HomePage.vue'
import InputForm from '@/components/InputForm.vue'
import ValidateCreative from '@/components/ValidateCreative.vue'
import GenerateCreative from '@/components/GenerateCreative.vue'
// import CanvasPage from '@/layouts/CanvasPage.vue'
import EditorPage from '@/layouts/EditorPage.vue'
import CreativePlayground from '@/components/CreativePlayground.vue'
import ProfileSelection from '@/views/Authentication/ProfileSelection.vue'
import Authentication from '@/views/Authentication/Authentication.vue'
import PageNotFound from '@/views/Error/PageNotFound.vue'

import AppService from '@/services/appService'
import { useAppStore } from '@/stores/app'
import { useAppCookies } from '@/composables/useCookies'
import errorCodes from '@/constants/errorCodes'
import InvalidRequestDataException from '@/errors/InvalidRequestDataException'
import TokenExpiredException from '@/errors/TokenExpiredException'
import TokenInvalidException from '@/errors/TokenInvalidException'
import UnauthorizedError from '@/errors/UnauthorizedError'
import InvalidPortalError from '@/errors/InvalidPortalError'
import ConvertCreative from '@/components/ConvertCreative.vue'
import { embedMessageTypes } from '@/constants/embed'

const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
      {
        path: '/',
        name: 'Default',
        component: Default,
        children: [
          {
            path: '/auth',
            name: 'Authentication',
            component: Authentication,
            children: [
              {
                path: '/profileSelection',
                name: 'ProfileSelection',
                component: ProfileSelection,
                meta: {
                  title: 'Profile Selection',
                },
              },
            ],
          },
          {
            path: 'app/:businessProfileId?',
            name: 'App',
            component: HomePage,
            beforeEnter: async (to, from, next) => {
              const businessProfileId = Number(to.params.businessProfileId)
              if (isNaN(businessProfileId) || businessProfileId <= 0) {
                // If a business profile id is not provided, redirect to page not found
                next({ name: 'NotFound' })
                return
              }
              if (to.matched[to.matched.length - 1].name === 'App') {
                // If there is no child route of app that was matched, redirect to input form page
                next({ name: 'Mode', params: { businessProfileId } })
                return
              }
              const appStore = useAppStore()
              if (appStore.businessProfiles.length === 0) {
                const appService = new AppService()
                appStore.setBusinessProfiles(
                  await appService.getPortalBusinessProfiles(
                    appStore.user?.id,
                    appStore.portal?.id,
                  ),
                )
              }
              appStore.setSelectedBusinessProfileId(Number(to.params.businessProfileId))
              next()
            },
            children: [
              {
                path: 'mode',
                name: 'Mode',
                component: InputForm,
              },
              {
                path: 'validate',
                name: 'Validate',
                component: ValidateCreative,
              },
              {
                path: 'generate',
                name: 'Generate',
                component: GenerateCreative,
              },
              {
                path: 'editor',
                name: 'Editor',
                component: EditorPage,
              },
              {
                path: 'download',
                name: 'Download',
                component: CreativePlayground,
              },
              {
                path: 'convert',
                name: 'Convert',
                component: ConvertCreative
              }
            ],
          },
          {
            path: '/not-found',
            name: 'NotFound',
            component: PageNotFound,
          },
        ],
      },
      {
        path: '/:pathMatch(.*)*',
        redirect: { name: 'NotFound' },
      },
    ],
  })

  router.beforeEach(async (to, from, next) => {
    const appStore = useAppStore()
    const appService = new AppService()
    const { getCookies, setCookies } = useAppCookies()

    // Support embed login with client token and client userId
    if (to.query.token) {
      // Remove previous tokens when loading the embed flow again
      appService.removeUserTokens()

      if (to.query.userId) {
        try {
          const embedLoginResponse = await appService.loginWithEmbedToken(
            to.query.token.toString(),
            to.query.userId.toString(),
            to.query.brandId?.toString(),
          )

          setCookies('accessToken', { accessToken: embedLoginResponse.accessToken })
          const clientUser = await appService.getUserById(embedLoginResponse.userId)
          setCookies('user', clientUser.data[0])

          if (to.query.brandId) {
            embedLoginResponse.brandId = +to.query.brandId
          }
          appStore.setEmbedUser(embedLoginResponse)
        } catch (e: any) {
          handleEmbedFlowError(e)
          appService.clearAuthData()
        }
      }
    }

    const user: any = getCookies('user')
    const accessToken: any = getCookies('accessToken')

    if (!user || !accessToken) {
      appService.clearAuthData()
    } else if (appStore.user == null) {
      appStore.setUser(user)
      Bugsnag.setUser(user.id, user.emailAddress, `${user.firstName} ${user.lastName}`)
      localStorage.setItem('accessToken', accessToken.accessToken)
    }

    if (appStore.portal == null) {
      const portalDomain = import.meta.env.DEV ? 'be.stunnr.co' : window.location.hostname
      try {
        appStore.setPortal(await appService.getPortalData(portalDomain))
      } catch (error) {
        console.error(error)
        if (error instanceof InvalidPortalError) {
          next({ name: 'NotFound' })
          return
        }
      }
    }

    const protectedRouteNames = ['Editor', 'Validate', 'Download', 'Generate', 'Convert']
    if (
      protectedRouteNames.includes(to.name as string) &&
      (!from.name || from.name === 'Default' || from.path === '/')
    ) {
      next({
        name: 'Mode',
        params: { businessProfileId: to.params.businessProfileId },
      })
    } else {
      next()
    }
  })

  return router
}

const handleEmbedFlowError = (e: any) => {
  let errorName = 'UnknownError'
  let errorCode = 500

  if (e instanceof InvalidRequestDataException) {
    errorName = InvalidRequestDataException.name
    errorCode = errorCodes.InvalidRequestDataException
  } else if (e instanceof TokenExpiredException) {
    errorName = TokenExpiredException.name
    errorCode = errorCodes.TokenExpiredException
  } else if (e instanceof TokenInvalidException) {
    errorName = TokenInvalidException.name
    errorCode = errorCodes.TokenInvalidException
  } else if (e instanceof UnauthorizedError) {
    errorName = UnauthorizedError.name
    errorCode = errorCodes.Unauthorized
  }

  window.top?.postMessage(
    { type: embedMessageTypes.EMBED_ERROR, name: errorName, code: errorCode, message: e.message },
    '*',
  )
}

export default createAppRouter
